import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import AutocompleteScopedCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteScopedCompaniesTypeahead';
import NumberInput from '~components/FormFields/v2/NumberInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  setTaxRateScheduleSchemaDefaultValues,
  TaxRateScheduleDto,
  TaxRateScheduleDtoAsyncError,
  taxRateScheduleSchema,
} from './taxRateScheduleSchema';

export type TaxRateScheduleFormHandler = {
  onSubmit: () => Promise<TaxRateScheduleDto | undefined>;
};

interface TaxRateScheduleFormProps {
  asyncErrors?: TaxRateScheduleDtoAsyncError[];
  isCompanyFieldLocked?: boolean;
  taxRateSchedule?: TaxRateSchedule;
}

const TaxRateScheduleForm = forwardRef<
  TaxRateScheduleFormHandler,
  TaxRateScheduleFormProps
>(function TaxRateScheduleForm(
  { asyncErrors, isCompanyFieldLocked, taxRateSchedule },
  ref,
) {
  const { userStore } = useStores();

  const isEditing = Boolean(taxRateSchedule);

  const { handleSubmit, control, setError, clearErrors } = useForm<TaxRateScheduleDto>({
    resolver: yupResolver(taxRateScheduleSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setTaxRateScheduleSchemaDefaultValues(userStore, taxRateSchedule),
  });

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  useEffect(() => {
    if (asyncErrors) {
      for (const error of asyncErrors) {
        setError(error.field as keyof TaxRateScheduleDto, { message: error.message });
      }
    }

    return () => {
      clearErrors();
    };
  }, [asyncErrors, clearErrors, setError]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <Box>
        <AutocompleteScopedCompaniesTypeahead
          companyId={getEffectiveUserCompanyId(userStore)}
          control={control}
          disableClearable
          isDisabled={isEditing || isCompanyFieldLocked}
          isRequired
          label={t('form_fields.company')}
          name="company"
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          isRequired
          label={t('form_fields.name')}
          name="name"
        />
      </Box>

      <Box>
        <NumberInput
          slotProps={{
            htmlInput: { min: 0, max: 100, step: 0.0001 },
            input: { endAdornment: '%' },
          }}
          control={control}
          label={t('form_fields.rate')}
          name="rate"
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          label={t('form_fields.external_accounting_id')}
          name="externalAccountingId"
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          label={t('form_fields.external_id')}
          name="externalId"
        />
      </Box>
    </Box>
  );
});

export default TaxRateScheduleForm;
